import {
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestLabelHierarchyNodeMappingPackage,
	FrontendCompanyTestLabels,
	FrontendCompanyTestLabelSecondaryLabels,
	FrontendCompanyTestLabelSettings,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrder,
	FrontendSampleInstance,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {
	CustomIconButton,
	GreenButton,
} from "../../../../common-components/button";
import { NavBar } from "../../../../common-components/navbar";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import {
	getLabelInputKey,
	getMatchingLabelsAndPackagesFromHierarchyTree,
	hasLabelPackageMatchingSampleInstance,
	LabelInputState,
} from "../../../../helpers/testLabelHelper";
import PrintLabelSamplePreviewTable from "./printLabelSamplePreviewTable";
import {
	CompanyLabelType,
	SecondaryCompanyLabelType,
	Severity,
	TestHierarchyNodeType,
} from "node-library/dist/constants";
import { getEnabledHierarchyNodeTree } from "../../../../helpers/testHierarchyHelper";
import { YellowWarnIcon } from "../../../../common-components/toast/warnIcons";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { CustomCheckbox } from "../../../../common-components/inputs";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	orderTestCategory: FrontendCompanyTestCategory;
	order: FrontendOrder;
	user: FrontendUserInfo | undefined;

	sampleInstances: Array<FrontendSampleInstance>;

	goBackToPreviousPage: () => void;
	goBackToOrderPage: () => void;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	updateSpecification: (spec: FrontendSampleSpecification) => void;
}

export default function PrintOrderLabelContent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		setCompany,
		orderTestCategory,
		order,
		goBackToPreviousPage,
		goBackToOrderPage,
		user,
		sampleInstances,
		specDirectories,
		updateSpecification,
	} = props;

	const hierarchyNodeParentMap = new Map<
		string,
		FrontendCompanyTestHierarchyNode[]
	>();

	orderTestCategory.hierarchyNodes.forEach((node) => {
		const parentId = node.parentNodeId || "root";
		if (!hierarchyNodeParentMap.has(parentId))
			hierarchyNodeParentMap.set(parentId, [node]);
		else {
			hierarchyNodeParentMap.get(parentId)!.push(node);
		}
	});

	const labelTestGroupNodeIdSet = useMemo(() => {
		const labelNodeIds: Set<string> = new Set(
			company.companyTestLabels.flatMap((label) => {
				return label.nodeMappingPackages.flatMap((pack) => {
					return pack.mappingNodeIds;
				});
			}),
		);

		return new Set(
			orderTestCategory.hierarchyNodes
				.filter((node) => {
					return (
						node.nodeType === TestHierarchyNodeType.TEST &&
						labelNodeIds.has(node.nodeId)
					);
				})
				.map((node) => node.parentNodeId)
				.filter((id): id is string => Boolean(id)), // filter out undefined/null
		);
	}, [orderTestCategory]);

	const [filterByTestGroupIds, setFilterByTestGroupIds] = useState<
		Array<string>
	>(Array.from(labelTestGroupNodeIdSet));

	const [mergeTestLabels, setMergeTestLabels] = useState<boolean>(false);

	const filteredSampleData: Array<{
		sampleInstance: FrontendSampleInstance;
		instanceHierarchyTree: TestHierarchyTreeNode;
		matchingLabelAndPackages: Array<{
			label: FrontendCompanyTestLabels;
			matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
		}>;
	}> = useMemo(() => {
		return sampleInstances
			.map((sampleInstance) => {
				const selectedNodeIds: string[] =
					sampleInstance.sampleTestInfo?.selectedTests.map(
						(info) => info.selectedNodeId,
					) ?? [];

				const instanceHierarchyTree = getEnabledHierarchyNodeTree(
					selectedNodeIds,
					hierarchyNodeParentMap,
					filterByTestGroupIds,
				);
				const unfilteredHierarchyTree = getEnabledHierarchyNodeTree(
					selectedNodeIds,
					hierarchyNodeParentMap,
					orderTestCategory.hierarchyNodes
						.filter((node) => {
							return node.nodeType === TestHierarchyNodeType.TEST_GROUP;
						})
						.map((node) => node.nodeId)
						.filter((id): id is string => Boolean(id)),
				);

				const matchingLabelAndPackages: Array<{
					label: FrontendCompanyTestLabels;
					matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
				}> = getMatchingLabelsAndPackagesFromHierarchyTree(
					company.companyTestLabels,
					instanceHierarchyTree,
					unfilteredHierarchyTree,
				);

				const shouldInclude =
					(instanceHierarchyTree.children.length > 0 ||
						sampleInstance.sampleTestInfo?.additionalTest) &&
					matchingLabelAndPackages.length > 0;

				if (!shouldInclude) return null;

				return {
					sampleInstance,
					instanceHierarchyTree,
					matchingLabelAndPackages,
				};
			})
			.filter(
				(
					item,
				): item is {
					sampleInstance: FrontendSampleInstance;
					instanceHierarchyTree: TestHierarchyTreeNode;
					matchingLabelAndPackages: Array<{
						label: FrontendCompanyTestLabels;
						matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
					}>;
				} => item !== null,
			);
	}, [sampleInstances, hierarchyNodeParentMap, filterByTestGroupIds]);

	const [selectedInstances, setSelectedInstances] = useState<Set<string>>(
		new Set(filteredSampleData.map((o) => o.sampleInstance.sampleInstanceId)),
	);

	const [labelInputMap, setLabelInputMap] = useState<LabelInputState>({});

	const isPrintDisabled = useMemo(() => {
		// If nothing selected
		if (selectedInstances.size === 0) return true;

		// For each selected sample, ensure required inputs are filled
		for (const item of filteredSampleData) {
			const { sampleInstance, matchingLabelAndPackages } = item;
			if (!selectedInstances.has(sampleInstance.sampleInstanceId)) continue;

			for (const labelAndPackage of matchingLabelAndPackages) {
				const { label, matchingPackages } = labelAndPackage;
				for (const secondaryLabel of label.secondaryLabels) {
					if (
						secondaryLabel.secondaryLabelType ===
						SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC
					) {
						for (const pkg of matchingPackages) {
							const key = getLabelInputKey(
								sampleInstance.sampleInstanceId,
								label.labelId,
								secondaryLabel.secondaryLabelId,
								pkg.packageId,
							);

							const value = labelInputMap[key];
							if (!value || value.trim() === "") {
								return true; // missing required input
							}
						}
					}
				}
			}
		}

		return false; // everything is valid
	}, [selectedInstances, filteredSampleData, labelInputMap]);

	useEffect(() => {
		setSelectedInstances((prevSelected) => {
			const validIds = new Set(
				filteredSampleData.map((item) => item.sampleInstance.sampleInstanceId),
			);

			const nextSelected = new Set<string>();
			for (const id of Array.from(prevSelected)) {
				if (validIds.has(id)) {
					nextSelected.add(id);
				}
			}

			// Only update state if something actually changed
			const isSame =
				nextSelected.size === prevSelected.size &&
				Array.from(nextSelected).every((id) => prevSelected.has(id));

			if (isSame) return prevSelected;

			return nextSelected;
		});
	}, [filteredSampleData]);

	function prepareLabel() {
		for (const item of filteredSampleData) {
			const { sampleInstance, matchingLabelAndPackages } = item;
			if (!selectedInstances.has(sampleInstance.sampleInstanceId)) continue;

			for (const labelAndPackage of matchingLabelAndPackages) {
				const { label, matchingPackages } = labelAndPackage;

				for (const secondaryLabel of label.secondaryLabels) {
					if (
						secondaryLabel.secondaryLabelType ===
						SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC
					) {
						for (const pkg of matchingPackages) {
							const key = getLabelInputKey(
								sampleInstance.sampleInstanceId,
								label.labelId,
								secondaryLabel.secondaryLabelId,
								pkg.packageId,
							);

							const value = labelInputMap[key];

							if (!value || value.trim() === "") {
								openSnackbar(
									translation("MissingInformation"),
									Severity.WARN,
									translation("PrintLabelMissingInformationError"),
									<YellowWarnIcon />,
								);
								return;
							}

							const numericValue = Number(value);
							if (isNaN(numericValue) || numericValue < 0) {
								openSnackbar(
									translation("InvalidInput"),
									Severity.ERROR,
									translation("InvalidNumericInputError"),
									<YellowWarnIcon />,
								);
								return;
							}
						}
					}
				}
			}
		}

		// If validation passes, you can now proceed with printing logic
		// openSnackbar("All inputs are valid. Proceeding to print...", "success");
		generateLabelHTMLFile();
	}
	//TODO: TEMP FUNCTION UNTIL NEW LABEL PRINTER ARRIVES!!
	function generateLabelHTMLFile() {
		const labelGroups = new Map<string, Map<string, string[]>>();
		// inner key = sampleInstanceId, outer key = sizeKey

		const multiDillutionLabelGroups = new Map<string, Map<number, string[]>>();
		// Outer key = sampleInstanceId, inner key = dilution level

		for (const item of filteredSampleData) {
			const {
				sampleInstance,
				matchingLabelAndPackages,
				instanceHierarchyTree,
			} = item;
			if (!selectedInstances.has(sampleInstance.sampleInstanceId)) continue;

			const sortedPackage = matchingLabelAndPackages.sort((a, b) => {
				const aHasInputSeq = a.label.secondaryLabels.some(
					(s) =>
						s.secondaryLabelType ===
						SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC,
				);
				const bHasInputSeq = b.label.secondaryLabels.some(
					(s) =>
						s.secondaryLabelType ===
						SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC,
				);

				if (aHasInputSeq !== bHasInputSeq) {
					return Number(bHasInputSeq) - Number(aHasInputSeq);
				}

				// Secondary sort (optional)
				return b.label.labelName.localeCompare(a.label.labelName);
			});
			for (const { label, matchingPackages } of sortedPackage) {
				const settings: FrontendCompanyTestLabelSettings = label.settings;
				if (label.labelType === CompanyLabelType.X_TYPE_SAMPLE_ID_LABEL) {
					const xTypeLabelHTML = `
													<div  class="label-wrapper" >

		<div  class="label">
			<div style="font-size: ${settings.idFontSize}px;">${sampleInstance.sample.fullSampleId}</div>
		</div>
		</div>
	`;
					const sizeKey = `${settings.width}x${settings.height}`;
					if (!labelGroups.has(sizeKey)) {
						labelGroups.set(sizeKey, new Map());
					}
					const sizeMap = labelGroups.get(sizeKey)!;
					if (!sizeMap.has(sampleInstance.sampleInstanceId)) {
						sizeMap.set(sampleInstance.sampleInstanceId, []);
					}
					sizeMap.get(sampleInstance.sampleInstanceId)!.push(xTypeLabelHTML);
					continue;
				}

				const shouldMerge: boolean =
					mergeTestLabels &&
					label.labelType === CompanyLabelType.MERGEABLE_TEST_METHOD_LABEL;
				const packagesToProcess: Array<
					Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>
				> = shouldMerge ? [matchingPackages] : matchingPackages.map((p) => [p]);

				for (const currentPackageGroup of packagesToProcess) {
					let row2Content = "";

					// Use the first package in the group for secondary label lookups and metadata
					// (In a merged label, IDs and inputs usually stem from the primary instance)
					const pkg = currentPackageGroup[0];
					const packageTestNode = orderTestCategory.hierarchyNodes.find(
						(node) => {
							return (
								node.nodeType === TestHierarchyNodeType.TEST &&
								pkg.mappingNodeIds.includes(node.nodeId)
							);
						},
					) as FrontendCompanyTestHierarchyNode;

					if (shouldMerge) {
						// MERGE LOGIC: Combine all short names into one string
						const mergedNames: string[] = currentPackageGroup
							.map((pkg) => {
								const node = orderTestCategory.hierarchyNodes.find(
									(n) =>
										n.nodeType === TestHierarchyNodeType.TEST &&
										pkg.mappingNodeIds.includes(n.nodeId),
								);
								return node?.nodeShortName || "";
							})
							.filter((name) => name !== "");

						row2Content = mergedNames.join("/<wbr>");
					} else {
						// ORIGINAL LOGIC: Single label processing

						if (
							label.labelType === CompanyLabelType.TEST_METHOD_LABEL ||
							label.labelType === CompanyLabelType.MERGEABLE_TEST_METHOD_LABEL
						) {
							row2Content = packageTestNode?.nodeShortName || "";
						} else if (label.labelType === CompanyLabelType.CUSTOM) {
							row2Content = `${label.primaryLabelText ?? ""} ${
								label.primaryLabelSubText ?? ""
							}`.trim();
						}
					}
					// First, split the secondary labels into two groups:
					const fixedSecondaryLabelsHTML: string[] = [];
					let inputSeqLabel: FrontendCompanyTestLabelSecondaryLabels | null =
						null;
					let inputSeqValue: number | null = null;

					for (const secondaryLabel of label.secondaryLabels) {
						const inputKey = getLabelInputKey(
							sampleInstance.sampleInstanceId,
							label.labelId,
							secondaryLabel.secondaryLabelId,
							pkg.packageId,
						);

						const secondaryContent = secondaryLabel.secondaryLabelText || "";
						let secondaryContentSubText = secondaryLabel.secondaryLabelSubText;

						switch (secondaryLabel.secondaryLabelType) {
							case SecondaryCompanyLabelType.FIXED_TEXT:
							case SecondaryCompanyLabelType.DILLUTION_AND_TEST_OPTION:
								if (
									secondaryLabel.secondaryLabelType ===
									SecondaryCompanyLabelType.DILLUTION_AND_TEST_OPTION
								) {
									//put the test option into it too
									const instanceSelectedOptionIds: Array<string> =
										sampleInstance.sampleTestInfo?.selectedTests.map((test) => {
											return test.selectedNodeId;
										}) as Array<string>;
									const secondaryContentSubTextArray: Array<string> = [];
									const testOptionGroupNodes:
										| FrontendCompanyTestHierarchyNode[]
										| undefined = hierarchyNodeParentMap.get(
										packageTestNode?.nodeId,
									);
									if (testOptionGroupNodes && testOptionGroupNodes.length > 0) {
										for (const groupNode of testOptionGroupNodes) {
											const testOptions = hierarchyNodeParentMap.get(
												groupNode.nodeId,
											);
											if (testOptions) {
												const selectedOptionNames: Array<string> = testOptions
													.filter((option) => {
														return (
															option.nodeType ===
																TestHierarchyNodeType.RADIO_OPTION &&
															instanceSelectedOptionIds.includes(option.nodeId)
														);
													})
													.map((option) => {
														return option.nodeName;
													});
												secondaryContentSubTextArray.push(
													...selectedOptionNames,
												);
											}
										}
									}
									secondaryContentSubText =
										secondaryContentSubTextArray.join(" ");
								}
								const html = !secondaryContentSubText
									? `<div style="font-size: ${secondaryLabel.fontSize}px;">${secondaryContent}</div>`
									: `<div style="display: flex; justify-content: space-between; width: 100%; padding: 0 10px; font-size: ${secondaryLabel.fontSize}px;">
						<span>${secondaryContent}</span>
						<span>${secondaryContentSubText}</span>
					</div>`;

								fixedSecondaryLabelsHTML.push(html);
								break;

							case SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC:
								const raw = labelInputMap[inputKey];
								const numeric = Number(raw);
								if (!isNaN(numeric) && numeric > 0) {
									inputSeqLabel = secondaryLabel;
									inputSeqValue = numeric;
								}
								break;
						}
					}
					const repeatCount = inputSeqValue ?? 1;

					for (let i = 0; i < repeatCount; i++) {
						let secondaryLabelHTML: string;
						if (inputSeqLabel) {
							const seqText = (i + 1).toString();
							secondaryLabelHTML = `<div style="font-size: ${inputSeqLabel.fontSize}px;">${seqText}</div>`;
						} else {
							secondaryLabelHTML = fixedSecondaryLabelsHTML.join("");
						}

						const labelHTML = `
								<div  class="label-wrapper">

		<div  class="label">
			<div style="font-size: ${settings.idFontSize}px;">${
				sampleInstance.sample.fullSampleId
			}</div>
			<div style="font-size: ${
				settings.labelFontSize - (shouldMerge ? 4 : 0)
			}px;">${row2Content}</div>
			${secondaryLabelHTML}
		</div>
		</div>
	`;

						const sizeKey = `${settings.width}x${settings.height}`;

						if (pkg.quantity == 1) {
							if (!labelGroups.has(sizeKey)) {
								labelGroups.set(sizeKey, new Map());
							}
							const sizeMap = labelGroups.get(sizeKey)!;
							if (!sizeMap.has(sampleInstance.sampleInstanceId)) {
								sizeMap.set(sampleInstance.sampleInstanceId, []);
							}
							sizeMap.get(sampleInstance.sampleInstanceId)!.push(labelHTML);
						} else {
							//goes to the dillution map instead
							const group = [];

							for (let j = 0; j < pkg.quantity; j++) {
								group.push(labelHTML);
							}

							if (
								!multiDillutionLabelGroups.has(sampleInstance.sampleInstanceId)
							) {
								multiDillutionLabelGroups.set(
									sampleInstance.sampleInstanceId,
									new Map(),
								);
							}
							const dillutionMap: Map<number, string[]> =
								multiDillutionLabelGroups.get(
									sampleInstance.sampleInstanceId,
								) as Map<number, string[]>;

							if (!dillutionMap.has(i + 1)) {
								dillutionMap.set(i + 1, group);
							} else {
								dillutionMap.get(i + 1)!.push(...group);
							}
						}
					}
				}
			}
		}

		for (const [sizeKey, instanceBlockMap] of Array.from(
			labelGroups.entries(),
		)) {
			const [widthStr, heightStr] = sizeKey.split("x");
			const width = parseFloat(widthStr);
			const height = parseFloat(heightStr);

			let isSmallLabel = false;
			for (const label of filteredSampleData.flatMap(
				(d) => d.matchingLabelAndPackages,
			)) {
				if (
					label.label.settings.width === width &&
					label.label.settings.height === height &&
					label.label.settings.groupName === "Small Label"
				) {
					isSmallLabel = true;
					break;
				}
			}
			let groupedBlocks: string[] = [];

			for (const [instanceId, blocks] of Array.from(instanceBlockMap)) {
				if (isSmallLabel) {
					//also render the multiDillutionLabelGroups label first
					const dilutionLabels: string[] = [];
					const instanceMultiDillutionLabelGroup: Map<number, string[]> =
						multiDillutionLabelGroups.get(instanceId)!;

					// Step 1: Get all labels sorted by dilutionKey descending
					if (instanceMultiDillutionLabelGroup) {
						const sortedDilutionKeys = Array.from(
							instanceMultiDillutionLabelGroup.keys(),
						).sort((a, b) => b - a);
						for (const key of sortedDilutionKeys) {
							const labels = instanceMultiDillutionLabelGroup.get(key)!;
							dilutionLabels.push(...labels);
						}
					}

					// Step 2: Chunk into columns (2-label vertical column per column slot)
					const columnChunks: string[][] = [];
					for (let i = 0; i < dilutionLabels.length; i += 2) {
						const chunk = dilutionLabels.slice(i, i + 2);
						while (chunk.length < 2) {
							chunk.push(`<div class="label empty-label"></div>`);
						}
						columnChunks.push(chunk); // this is a vertical column of 2 labels
					}

					// Step 3: Now pair every two columnChunks into a .label-group
					const median: number = Math.ceil(columnChunks.length / 2);
					for (let i = 0; i < median; i++) {
						const left = columnChunks[i];
						const right = columnChunks[median + i] ?? [
							`<div class="label empty-label"></div>`,
							`<div class="label empty-label"></div>`,
						];

						const group = `
			<div class="label-group">
				${left[0]}
				${right[0]}
				${left[1]}
				${right[1]}
			</div>
		`;
						groupedBlocks.push(group);
					}

					for (let i = 0; i < blocks.length; i += 4) {
						const group = blocks.slice(i, i + 4);
						while (group.length < 4) {
							group.push(`<div class="label empty-label"></div>`);
						}
						groupedBlocks.push(
							`<div class="label-group">${group.join("")}</div>`,
						);
					}
				} else {
					groupedBlocks.push(...blocks); // Use normal labels
				}
			}
			const htmlContent = `
		<!DOCTYPE html>
		<html>
		<head>
			<title>Labels (${sizeKey})</title>
			<style>
				body {
    margin: 0;
    padding: 0;
    font-family: sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #f9f9f9;
  }

  .label-wrapper {
	  background-color: white;
	      width: ${width}mm;
    height: ${height}mm;
  }

  .label-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    width: ${width * 2 + 1}mm;
    height: ${height * 2 + 1}mm;
    box-sizing: border-box;
    page-break-after: always;
gap: 1px;
background-color: black;
padding: 1px 0px 0px 1px;
  }

  .label {
    width: ${width}mm;
    height: ${height}mm;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
    text-align: center;
    font-weight: bold;
    font-size: 11px;
    box-sizing: border-box;
	padding: 0px 10px;
  }

  .empty-label {
    border: none;
    background: white;
  }

  @media print {
    @page {
      size: ${isSmallLabel ? width * 2 + 1 : width}mm ${
				isSmallLabel ? height * 2 + 1 : height
			}mm;
      margin: 0;
    }
	  html, body, .label, .label-group {
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}

    .label-wrapper {
      page-break-inside: avoid;
      break-inside: avoid;
      page-break-after: ${isSmallLabel ? "avoid" : "always"};
      
	  background-color: white;
    }

    .label-group {
      page-break-inside: avoid;
      break-inside: avoid;
	        page-break-after: always;
      break-after: page;
    }
  }
			</style>
		</head>
		<body>
  ${groupedBlocks.join("")}
		</body>
		</html>
	`;

			const newWindow = window.open();
			if (newWindow) {
				newWindow.document.open();
				newWindow.document.write(htmlContent);
				newWindow.document.close();
				newWindow.focus();
				// Optionally auto-trigger print
				// newWindow.print();
			} else {
				openSnackbar(
					translation("PopupBlocked"),
					Severity.ERROR,
					translation("PopupBlockedDescription"),
					<RedErrorIcon />,
				);
			}
		}
	}

	// function generateLabelHTMLFile() {
	// 	const labelGroups = new Map<string, string[]>(); // key = `${width}x${height}`, value = labelHTML[]

	// 	for (const item of filteredSampleData) {
	// 		const {
	// 			sampleInstance,
	// 			matchingLabelAndPackages,
	// 			instanceHierarchyTree,
	// 		} = item;
	// 		if (!selectedInstances.has(sampleInstance.sampleInstanceId)) continue;

	// 		for (const { label, matchingPackages } of matchingLabelAndPackages) {
	// 			const settings: FrontendCompanyTestLabelSettings = label.settings;
	// 			if (label.labelType === CompanyLabelType.X_TYPE_SAMPLE_ID_LABEL) {
	// 				const xTypeLabelHTML = `
	// 												<div  class="label-wrapper" >

	// 	<div  class="label">
	// 		<div style="font-size: ${settings.idFontSize}px;">${sampleInstance.sample.fullSampleId}</div>
	// 	</div>
	// 	</div>
	// `;
	// 				const sizeKey = `${settings.width}x${settings.height}`;
	// 				if (!labelGroups.has(sizeKey)) {
	// 					labelGroups.set(sizeKey, []);
	// 				}
	// 				labelGroups.get(sizeKey)!.push(xTypeLabelHTML);
	// 				continue;
	// 			}

	// 			for (const pkg of matchingPackages) {
	// 				let row2Content = "";
	// 				const packageTestNode: FrontendCompanyTestHierarchyNode =
	// 					orderTestCategory.hierarchyNodes.find((node) => {
	// 						return (
	// 							node.nodeType === TestHierarchyNodeType.TEST &&
	// 							pkg.mappingNodeIds.includes(node.nodeId)
	// 						);
	// 					}) as FrontendCompanyTestHierarchyNode;
	// 				if (label.labelType === CompanyLabelType.TEST_METHOD_LABEL) {
	// 					// Attempt to find matching node name from mappingNodeIds
	// 					const nodeShortName: string =
	// 						packageTestNode?.nodeShortName as string;

	// 					row2Content = nodeShortName;
	// 				} else if (label.labelType === CompanyLabelType.CUSTOM) {
	// 					row2Content = `${label.primaryLabelText ?? ""} ${
	// 						label.primaryLabelSubText ?? ""
	// 					}`.trim();
	// 				}
	// 				// First, split the secondary labels into two groups:
	// 				const fixedSecondaryLabelsHTML: string[] = [];
	// 				let inputSeqLabel: FrontendCompanyTestLabelSecondaryLabels | null =
	// 					null;
	// 				let inputSeqValue: number | null = null;

	// 				for (const secondaryLabel of label.secondaryLabels) {
	// 					const inputKey = getLabelInputKey(
	// 						sampleInstance.sampleInstanceId,
	// 						label.labelId,
	// 						secondaryLabel.secondaryLabelId,
	// 						pkg.packageId
	// 					);

	// 					const secondaryContent = secondaryLabel.secondaryLabelText || "";
	// 					let secondaryContentSubText = secondaryLabel.secondaryLabelSubText;

	// 					switch (secondaryLabel.secondaryLabelType) {
	// 						case SecondaryCompanyLabelType.FIXED_TEXT:
	// 						case SecondaryCompanyLabelType.DILLUTION_AND_TEST_OPTION:
	// 							if (
	// 								secondaryLabel.secondaryLabelType ===
	// 								SecondaryCompanyLabelType.DILLUTION_AND_TEST_OPTION
	// 							) {
	// 								//put the test option into it too
	// 								const instanceSelectedOptionIds: Array<string> =
	// 									sampleInstance.sampleTestInfo?.selectedTests.map((test) => {
	// 										return test.selectedNodeId;
	// 									}) as Array<string>;
	// 								const secondaryContentSubTextArray: Array<string> = [];
	// 								const testOptionGroupNodes:
	// 									| FrontendCompanyTestHierarchyNode[]
	// 									| undefined = hierarchyNodeParentMap.get(
	// 									packageTestNode?.nodeId
	// 								);
	// 								if (testOptionGroupNodes && testOptionGroupNodes.length > 0) {
	// 									for (const groupNode of testOptionGroupNodes) {
	// 										const testOptions = hierarchyNodeParentMap.get(
	// 											groupNode.nodeId
	// 										);
	// 										if (testOptions) {
	// 											const selectedOptionNames: Array<string> = testOptions
	// 												.filter((option) => {
	// 													return (
	// 														option.nodeType ===
	// 															TestHierarchyNodeType.RADIO_OPTION &&
	// 														instanceSelectedOptionIds.includes(option.nodeId)
	// 													);
	// 												})
	// 												.map((option) => {
	// 													return option.nodeName;
	// 												});
	// 											secondaryContentSubTextArray.push(
	// 												...selectedOptionNames
	// 											);
	// 										}
	// 									}
	// 								}
	// 								secondaryContentSubText =
	// 									secondaryContentSubTextArray.join(" ");
	// 							}
	// 							const html = !secondaryContentSubText
	// 								? `<div style="font-size: ${secondaryLabel.fontSize}px;">${secondaryContent}</div>`
	// 								: `<div style="display: flex; justify-content: space-between; width: 100%; padding: 0 10px; font-size: ${secondaryLabel.fontSize}px;">
	// 					<span>${secondaryContent}</span>
	// 					<span>${secondaryContentSubText}</span>
	// 				</div>`;

	// 							fixedSecondaryLabelsHTML.push(html);
	// 							break;

	// 						case SecondaryCompanyLabelType.INPUT_SEQ_NUMERIC:
	// 							const raw = labelInputMap[inputKey];
	// 							const numeric = Number(raw);
	// 							if (!isNaN(numeric) && numeric > 0) {
	// 								inputSeqLabel = secondaryLabel;
	// 								inputSeqValue = numeric;
	// 							}
	// 							break;
	// 					}
	// 				}
	// 				const repeatCount = inputSeqValue ?? 1;

	// 				for (let i = 0; i < repeatCount; i++) {
	// 					let secondaryLabelHTML: string;
	// 					if (inputSeqLabel) {
	// 						const seqText = (i + 1).toString();
	// 						secondaryLabelHTML = `<div style="font-size: ${inputSeqLabel.fontSize}px;">${seqText}</div>`;
	// 					} else {
	// 						secondaryLabelHTML = fixedSecondaryLabelsHTML.join("");
	// 					}

	// 					const labelHTML = `
	// 							<div  class="label-wrapper">

	// 	<div  class="label">
	// 		<div style="font-size: ${settings.idFontSize}px;">${sampleInstance.sample.fullSampleId}</div>
	// 		<div style="font-size: ${settings.labelFontSize}px;">${row2Content}</div>
	// 		${secondaryLabelHTML}
	// 	</div>
	// 	</div>
	// `;

	// 					// Only push once (no additional .quantity multiplier)
	// 					for (let j = 0; j < pkg.quantity; j++) {
	// 						const sizeKey = `${settings.width}x${settings.height}`;
	// 						if (!labelGroups.has(sizeKey)) {
	// 							labelGroups.set(sizeKey, []);
	// 						}
	// 						labelGroups.get(sizeKey)!.push(labelHTML);
	// 					}
	// 				}
	// 			}
	// 		}
	// 	}

	// 	for (const [sizeKey, blocks] of Array.from(labelGroups.entries())) {
	// 		const htmlContent = `
	// 	<!DOCTYPE html>
	// 	<html>
	// 	<head>
	// 		<title>Labels (${sizeKey})</title>
	// 		<style>
	// 			 body {
	//   margin: 0;
	//   padding: 0;
	//   font-family: sans-serif;
	//   display: flex;
	//   flex-direction: column;
	//   align-items: center;
	//   background-color: #f9f9f9;
	// }

	// .label-wrapper{

	// 		display: grid;

	// }
	// .label {
	//       width: ${sizeKey.split("x")[0]}mm;
	//   	height: ${sizeKey.split("x")[1]}mm;
	//   display: flex;
	//   flex-direction: column;
	//   justify-content: space-evenly;
	//   align-items: center;
	//   text-align: center;
	//   font-weight: bold;
	//   font-size: 11px;
	//   box-sizing: border-box;
	//   page-break-after: always;
	// 	border: 1px solid black;

	// }

	// @media print {
	//   @page {
	//     size: ${sizeKey.split("x")[0]}mm ${sizeKey.split("x")[1]}mm;
	//     margin: 0;
	//   }

	//   body {
	//     margin: 0;
	//     padding: 0;
	//     background-color: white;
	//   }

	//   	.label-wrapper{
	// 		height: 100vh;
	// 		    page-break-inside: avoid;
	// break-inside: avoid;

	// page-break-after: always;
	// break-after: page;
	// display: grid;
	// }
	//   .label {

	// display: flex;
	// flex-direction: column;
	//   justify-content: space-evenly;
	// align-items: center;

	// box-sizing: border-box;
	// font-family: sans-serif;
	// 			border: 1px solid black;

	//   }
	// }
	// 		</style>
	// 	</head>
	// 	<body>
	// 		${blocks.join("")}
	// 	</body>
	// 	</html>
	// `;

	// 		const newWindow = window.open();
	// 		if (newWindow) {
	// 			newWindow.document.open();
	// 			newWindow.document.write(htmlContent);
	// 			newWindow.document.close();
	// 			newWindow.focus();
	// 			// Optionally auto-trigger print
	// 			// newWindow.print();
	// 		} else {
	// 			openSnackbar(
	// 				translation("PopupBlocked"),
	// 				Severity.ERROR,
	// 				translation("PopupBlockedDescription"),
	// 				<RedErrorIcon />
	// 			);
	// 		}
	// 	}
	// }

	const navbarItems: Array<{
		label: string;
		onClick: () => void;
		isCurrentItem: boolean;
	}> = [
		{
			label: translation("Orders"),
			onClick: () => goBackToOrderPage(),
			isCurrentItem: false,
		},
		{
			label: translation("OrderDetails"),
			onClick: () => goBackToPreviousPage(),
			isCurrentItem: false,
		},
		{
			label: translation("PrintLabels"),
			onClick: () => {},
			isCurrentItem: true,
		},
	];

	let keyActionOption: {
		key: string;
		onClick: () => void;
		children: any;
		disabled?: boolean;
	} = {
		key: "print-labels",
		onClick: () => {
			prepareLabel();
		},
		children: translation("Print"),
		disabled: isPrintDisabled,
	};

	function handleLabelInputChange(
		sampleInstanceId: string,
		labelId: string,
		secondaryLabelId: string,
		packageId: string,
		value: string,
	) {
		const key = getLabelInputKey(
			sampleInstanceId,
			labelId,
			secondaryLabelId,
			packageId,
		);
		setLabelInputMap((prev) => ({
			...prev,
			[key]: value,
		}));
	}

	const buttonPanel: React.ReactNode = (
		<div
			style={{
				display: "flex",
				gap: "5px",
				justifyContent: "center",
				flexWrap: "wrap",
			}}
		>
			<CustomCheckbox
				className={`test-group-option-filter-checkbox ${
					mergeTestLabels && "test-group-option-filter-checkbox-checked"
				}`}
				label={translation("MergeLargeLabels")}
				checked={mergeTestLabels}
				setChecked={() => setMergeTestLabels(!mergeTestLabels)}
			/>
			{
				<GreenButton
					id={"order-details-key-action-button"}
					onClick={keyActionOption.onClick}
					disabled={keyActionOption.disabled}
				>
					{keyActionOption.children}
				</GreenButton>
			}
		</div>
	);

	const orderDetailsHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "10px",
				flexWrap: "wrap",
			}}
		>
			<div>
				<div style={{ marginLeft: "35px" }}>
					<NavBar items={navbarItems}></NavBar>
				</div>

				<div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
					<CustomIconButton
						id="order-details-back-button"
						size="small"
						onClick={() => {
							goBackToPreviousPage();
						}}
						icon={<KeyboardArrowLeftIcon />}
					></CustomIconButton>

					<div className="typography-heading-01">
						{`${translation("Order")} ${order.orderBarcodeId} - ${translation(
							"PrintLabels",
						)}`}
					</div>
				</div>
			</div>
			{buttonPanel}
		</div>
	);

	let viewSampleTable: JSX.Element = (
		<PrintLabelSamplePreviewTable
			company={company}
			sampleInstances={sampleInstances}
			orderTestCategory={orderTestCategory}
			hierarchyNodeParentMap={hierarchyNodeParentMap}
			labelTestGroupNodeIdSet={labelTestGroupNodeIdSet}
			filterByTestGroupIds={filterByTestGroupIds}
			setFilterByTestGroupIds={setFilterByTestGroupIds}
			filteredSampleData={filteredSampleData}
			selectedInstances={selectedInstances}
			setSelectedInstances={setSelectedInstances}
			labelInputMap={labelInputMap}
			handleLabelInputChange={handleLabelInputChange}
			setCompany={setCompany}
			user={user as FrontendUserInfo}
			specDirectories={specDirectories}
			updateSpecification={updateSpecification}
		/>
	);

	const sampleDetailsBody: React.ReactNode = <div>{viewSampleTable}</div>;

	return (
		<div style={{ margin: "0 auto" }}>
			<div>
				{orderDetailsHeaderRow}

				{sampleDetailsBody}
			</div>
		</div>
	);
}
